import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { 
  AlertTriangle, 
  Save, 
  MapPin, 
  FileText, 
  MessageCircle,
  Calendar,
  Coins,
  Loader2,
  CornerDownRight,
  ArrowLeft,
  CheckCircle,
  XCircle,
  HelpCircle,
  BadgeDollarSign,
  BadgeEuro,
  BadgePoundSterling,
  SendHorizontal,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  JobProposalWithDetails, 
  useUpdateJobProposal,
  UpdateJobProposalData 
} from '@/hooks/useJobProposals';
import { QuoteForm } from '@/components/business/QuoteForm';
import { useCommunity } from '@/contexts/CommunityContext';
import { PATH } from '@/routes/routeConfig';
import { encodeHashData, decodeHashData, cleanFormData, updateUrlHash, getCurrentUrlHash, debounce } from '@/lib/hashUtils';
import { getUrgencyDisplay as getUrgencyDisplayUtil } from '@communitly/shared';

// Validation function for quote pricing
const validateQuotePricing = (
  quoteFormData: any,
  quoteItems: any[],
  currency: string
): { isValid: boolean; errorMessage: string } => {
  if (!quoteFormData.price_type || quoteFormData.price_type === 'no_quote') {
    return { isValid: true, errorMessage: '' };
  }

  // Check for zero or negative prices first
  if (quoteFormData.price_type === 'fixed' && quoteFormData.fixed_price) {
    const fixedPrice = parseFloat(quoteFormData.fixed_price);
    if (fixedPrice <= 0) {
      return {
        isValid: false,
        errorMessage: 'Fixed price cannot be zero or negative. For free services, use "No Quote" option and explain in your response message.'
      };
    }
  }

  if (quoteFormData.price_type === 'estimate' && quoteFormData.estimated_price) {
    const estimatedPrice = parseFloat(quoteFormData.estimated_price);
    if (estimatedPrice <= 0) {
      return {
        isValid: false,
        errorMessage: 'Estimated price cannot be zero or negative. For free services, use "No Quote" option and explain in your response message.'
      };
    }
  }

  if (quoteFormData.price_type === 'range') {
    if (quoteFormData.min_price) {
      const minPrice = parseFloat(quoteFormData.min_price);
      if (minPrice <= 0) {
        return {
          isValid: false,
          errorMessage: 'Minimum price cannot be zero or negative. For free services, use "No Quote" option and explain in your response message.'
        };
      }
    }
    if (quoteFormData.max_price) {
      const maxPrice = parseFloat(quoteFormData.max_price);
      if (maxPrice <= 0) {
        return {
          isValid: false,
          errorMessage: 'Maximum price cannot be zero or negative. For free services, use "No Quote" option and explain in your response message.'
        };
      }
    }
  }

  // Check breakdown validation if items exist
  if (quoteItems.length > 0) {
    const subtotal = quoteItems.reduce((sum, item) => sum + item.line_total, 0);
    
    if (quoteFormData.price_type === 'fixed' && quoteFormData.fixed_price) {
      const fixedPrice = parseFloat(quoteFormData.fixed_price);
      if (fixedPrice !== subtotal) {
        return {
          isValid: false,
          errorMessage: `Fixed price must equal breakdown subtotal (${currency}${subtotal.toFixed(2)})`
        };
      }
    }

    if (quoteFormData.price_type === 'estimate' && quoteFormData.estimated_price) {
      const estimatedPrice = parseFloat(quoteFormData.estimated_price);
      if (estimatedPrice !== subtotal) {
        return {
          isValid: false,
          errorMessage: `Estimate must equal breakdown subtotal (${currency}${subtotal.toFixed(2)})`
        };
      }
    }

    if (quoteFormData.price_type === 'range') {
      if (quoteFormData.min_price) {
        const minPrice = parseFloat(quoteFormData.min_price);
        if (minPrice !== subtotal) {
          return {
            isValid: false,
            errorMessage: `Range minimum must equal breakdown subtotal (${currency}${subtotal.toFixed(2)})`
          };
        }
      }
      if (quoteFormData.max_price) {
        const maxPrice = parseFloat(quoteFormData.max_price);
        const minPrice = parseFloat(quoteFormData.min_price || '0');
        if (maxPrice < minPrice) {
          return {
            isValid: false,
            errorMessage: `Maximum price cannot be less than minimum price (${currency}${minPrice.toFixed(2)})`
          };
        }
      }
    }
  }

  return { isValid: true, errorMessage: '' };
};

const JobProposalDetail = () => {
  const { proposalId } = useParams<{ proposalId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { getParameter } = useCommunity();
  const currency = getParameter<string>('currency') || '$';
  
  // Get appropriate currency icon based on community currency
  const getCurrencyIcon = () => {
    switch (currency) {
      case '£':
        return BadgePoundSterling;
      case '€':
      case 'EUR':
        return BadgeEuro;
      case '$':
      case 'USD':
        return BadgeDollarSign;
      default:
        return Coins; // Generic fallback
    }
  };
  
  const CurrencyIcon = getCurrencyIcon();
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingSection, setEditingSection] = useState<'quote' | 'response' | null>(null);
  const [skipQuote, setSkipQuote] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [savedQuoteSummary, setSavedQuoteSummary] = useState<any>(null);
  const [quoteFormData, setQuoteFormData] = useState({
    price_type: '' as 'fixed' | 'estimate' | 'range' | 'no_quote' | '',
    is_partial: false,
    fixed_price: '',
    estimated_price: '',
    min_price: '',
    max_price: '',
    notes: ''
  });
  const [quoteItems, setQuoteItems] = useState<any[]>([]);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  // Debounced function to save form data to URL hash
  const saveToHash = useCallback((hashData: any, currentProposalId: string) => {
    if (currentProposalId && (
      (hashData.formData.response_message && hashData.formData.response_message.trim()) || 
      hashData.formData.status || 
      hashData.quoteFormData || 
      hashData.quoteItems.length > 0
    )) {
      const completeHashData = {
        ...hashData,
        proposalId: currentProposalId,
        timestamp: Date.now()
      };
      const hash = encodeHashData(completeHashData);
      updateUrlHash(hash);
    }
  }, []);

  const debouncedSaveToHash = useRef(debounce(saveToHash, 1000)).current;
  
  // Form data state
  const [formData, setFormData] = useState({
    status: '' as 'PROPOSED' | 'DECLINED' | 'REQUESTED_INFO' | 'AWAITING_RESPONSE' | '',
    response_message: '',
    quote: null as number | null,
    comment: ''
  });

  // Fetch proposal data
  const { data: proposal, isLoading, error } = useQuery({
    queryKey: ['job-proposal', proposalId],
    queryFn: async (): Promise<JobProposalWithDetails | null> => {
      if (!proposalId) return null;
      
      const { data, error } = await (supabase as any)
        .from('job_proposals')
        .select(`
          id,
          service_request_id,
          business_id,
          quote,
          response_message,
          status,
          comment,
          created_at,
          responded_at,
          service_request:service_requests (
            id,
            original_request,
            town,
            urgency,
            created_at,
            attachments:service_request_attachments (
              id,
              image_url,
              original_filename
            )
          ),
          detailed_quote:quotes (
            id,
            price_type,
            status,
            fixed_price,
            estimated_price,
            min_price,
            max_price,
            notes,
            created_at,
            updated_at,
            quote_items (
              id,
              label,
              quantity,
              unit_label,
              unit_price,
              line_total,
              display_order
            )
          )
        `)
        .eq('id', proposalId)
        .single();
        
      if (error) throw error;
      return data;
    },
    enabled: !!proposalId
  });

  // Load form data from URL hash on page load (only for awaiting response proposals)
  useEffect(() => {
    if (proposalId && proposal && proposal.status === 'AWAITING_RESPONSE' && isInitializing) {
      const hash = getCurrentUrlHash();
      if (hash) {
        const hashData = decodeHashData<{
          proposalId?: string;
          formData?: typeof formData;
          quoteFormData?: any;
          quoteItems?: any[];
          editingSection?: 'quote' | 'response' | null;
          savedQuoteSummary?: any;
        }>(hash);
        
        if (hashData?.proposalId === proposalId) {
          console.log('Loading from hash:', hashData);
          
          // Restore main form data
          if (hashData.formData) {
            setFormData({
              status: hashData.formData.status || '',
              response_message: hashData.formData.response_message || '',
              quote: hashData.formData.quote || null,
              comment: hashData.formData.comment || ''
            });
          }
          
          // Restore quote form data
          if (hashData.quoteFormData) {
            setQuoteFormData(hashData.quoteFormData);
          }
          
          // Restore quote items
          if (hashData.quoteItems) {
            setQuoteItems(hashData.quoteItems);
          }
          
          // Don't restore showBreakdown - always start collapsed
          // (User can expand quote breakdown if needed)
          
          // Don't restore editing section - always start collapsed
          // (User can expand quote section if needed)
          
          // Restore saved quote summary
          if (hashData.savedQuoteSummary) {
            setSavedQuoteSummary(hashData.savedQuoteSummary);
          }
        }
      }
      
      // Mark initialization as complete
      setIsInitializing(false);
    }
  }, [proposalId, proposal, isInitializing]);

  // Initialize form data when proposal loads (only during initialization)
  useEffect(() => {
    if (proposal && isInitializing && proposal.status !== 'AWAITING_RESPONSE') {
      setFormData({
        status: proposal.status as 'PROPOSED' | 'DECLINED' | 'REQUESTED_INFO' | '',
        response_message: proposal.response_message || '',
        quote: proposal.quote,
        comment: proposal.comment || ''
      });
      setIsInitializing(false);
    }
  }, [proposal, isInitializing]);

  // Initialize quote form data from existing quote
  useEffect(() => {
    if (proposal?.detailed_quote && isInitializing) {
      const quote = proposal.detailed_quote;
      setQuoteFormData({
        price_type: quote.price_type,
        is_partial: quote.status === 'partial',
        fixed_price: quote.fixed_price?.toString() || '',
        estimated_price: quote.estimated_price?.toString() || '',
        min_price: quote.min_price?.toString() || '',
        max_price: quote.max_price?.toString() || '',
        notes: quote.notes || ''
      });

      if (quote.quote_items) {
        setQuoteItems(quote.quote_items.map(item => ({
          id: item.id,
          label: item.label,
          quantity: item.quantity,
          unit_label: item.unit_label,
          unit_price: item.unit_price,
          line_total: item.line_total,
          display_order: item.display_order
        })));
        // Don't auto-expand breakdown - keep collapsed by default
      }
    }
  }, [proposal, isInitializing]);

  // Auto-generate quote summary when quote form data changes (separate from hash saving to prevent infinite loops)
  useEffect(() => {
    if (proposal && proposal.status === 'AWAITING_RESPONSE' && proposalId && !isInitializing) {
      if (quoteFormData.price_type && quoteFormData.price_type !== 'no_quote') {
        // Use dedicated validation function
        const validation = validateQuotePricing(quoteFormData, quoteItems, currency);
        
        if (validation.isValid) {
          const autoQuoteSummary = {
            price_type: quoteFormData.price_type,
            fixed_price: quoteFormData.price_type === 'fixed' && quoteFormData.fixed_price ? parseFloat(quoteFormData.fixed_price) : null,
            estimated_price: quoteFormData.price_type === 'estimate' && quoteFormData.estimated_price ? parseFloat(quoteFormData.estimated_price) : null,
            min_price: quoteFormData.price_type === 'range' && quoteFormData.min_price ? parseFloat(quoteFormData.min_price) : null,
            max_price: quoteFormData.price_type === 'range' && quoteFormData.max_price ? parseFloat(quoteFormData.max_price) : null,
            notes: quoteFormData.notes.trim() || null,
            is_partial: quoteFormData.is_partial,
            items_count: quoteItems.length,
            validation_error: null
          };
          setSavedQuoteSummary(autoQuoteSummary);
        } else {
          // Set validation error state
          setSavedQuoteSummary({
            price_type: quoteFormData.price_type,
            fixed_price: null,
            estimated_price: null,
            min_price: null,
            max_price: null,
            notes: quoteFormData.notes.trim() || null,
            is_partial: quoteFormData.is_partial,
            items_count: quoteItems.length,
            validation_error: validation.errorMessage
          });
        }
      } else if (quoteFormData.price_type === 'no_quote') {
        setSavedQuoteSummary({
          price_type: 'no_quote',
          fixed_price: null,
          estimated_price: null,
          min_price: null,
          max_price: null,
          notes: quoteFormData.notes.trim() || null,
          is_partial: false,
          items_count: 0
        });
      } else if (!quoteFormData.price_type) {
        setSavedQuoteSummary(null);
      }
    }
  }, [quoteFormData, quoteItems, proposal, proposalId, isInitializing]);

  // Auto-update prices based on breakdown total when items change
  useEffect(() => {
    if (proposal && proposal.status === 'AWAITING_RESPONSE' && !isInitializing && quoteItems.length > 0) {
      const subtotal = quoteItems.reduce((sum, item) => sum + item.line_total, 0);
      const subtotalStr = subtotal.toFixed(2);
      
      // Auto-update prices based on quote type
      if (quoteFormData.price_type === 'fixed') {
        // Fixed price should always equal breakdown
        if (quoteFormData.fixed_price !== subtotalStr) {
          setQuoteFormData(prev => ({ ...prev, fixed_price: subtotalStr }));
        }
      } else if (quoteFormData.price_type === 'estimate') {
        // Estimate should always equal breakdown
        if (quoteFormData.estimated_price !== subtotalStr) {
          setQuoteFormData(prev => ({ ...prev, estimated_price: subtotalStr }));
        }
      } else if (quoteFormData.price_type === 'range') {
        // Range min should always equal breakdown
        if (quoteFormData.min_price !== subtotalStr) {
          setQuoteFormData(prev => {
            // Also update max price if it's less than the new min
            const newMin = parseFloat(subtotalStr);
            const currentMax = prev.max_price ? parseFloat(prev.max_price) : 0;
            
            return {
              ...prev,
              min_price: subtotalStr,
              // Ensure max is at least equal to min
              max_price: currentMax < newMin ? subtotalStr : prev.max_price
            };
          });
        }
      }
    }
  }, [quoteItems, quoteFormData.price_type, proposal, isInitializing]);

  // Save form data to hash with debouncing (only for awaiting response proposals after initialization)
  useEffect(() => {
    // Only save after initialization is complete and we have proposal data
    if (proposal && proposal.status === 'AWAITING_RESPONSE' && proposalId && !isInitializing) {
      // Only save if there's meaningful data
      const hasData = formData.status || 
                     (formData.response_message && formData.response_message.trim()) || 
                     (formData.comment && formData.comment.trim()) ||
                     quoteFormData.price_type || 
                     quoteItems.length > 0 ||
                     showBreakdown ||
                     editingSection ||
                     savedQuoteSummary;
      
      if (hasData) {
        const hashData = {
          formData: {
            status: formData.status,
            response_message: formData.response_message,
            quote: formData.quote,
            comment: formData.comment
          },
          quoteFormData,
          quoteItems,
          showBreakdown,
          editingSection,
          savedQuoteSummary
        };
        debouncedSaveToHash(hashData, proposalId);
      }
    }
  }, [formData, quoteFormData, quoteItems, showBreakdown, editingSection, proposal, proposalId, isInitializing, debouncedSaveToHash]);

  // Reset skip quote when status changes away from PROPOSED
  useEffect(() => {
    if (formData.status !== 'PROPOSED') {
      setSkipQuote(false);
    }
  }, [formData.status]);


  const updateProposalMutation = useUpdateJobProposal();

  // Function to navigate back - hash stays on this page only
  const navigateBackWithHash = useCallback(() => {
    // Don't transfer hash to job opportunities page - it's not proposal-specific
    navigate(PATH.ADMIN_MY_JOB_OPPORTUNITIES);
  }, [navigate]);

  const handleSaveResponse = () => {
    if (!proposal) return;
    
    // Show confirmation modal
    setShowConfirmModal(true);
  };

  const handleConfirmResponse = async () => {
    if (!proposal) return;
    setShowConfirmModal(false);

    if (formData.status === 'DECLINED' && !formData.response_message.trim()) {
      toast({
        title: "Error", 
        description: "Please provide a reason when declining a request.",
        variant: "destructive"
      });
      return;
    }

    if (formData.status === 'REQUESTED_INFO' && !formData.response_message.trim()) {
      toast({
        title: "Error",
        description: "Please specify what information you need.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    // Prepare the payload for the submit-proposal-response edge function
    const payload: any = {
      proposal_id: proposal.id,
      status: formData.status,
      response_message: formData.response_message.trim() || null,
      comment: formData.comment.trim() || null
    };

    // Add quote data if status is PROPOSED and we have quote information
    if (formData.status === 'PROPOSED' && savedQuoteSummary && !savedQuoteSummary.validation_error) {
      payload.quote_data = {
        price_type: savedQuoteSummary.price_type,
        is_partial: savedQuoteSummary.is_partial,
        fixed_price: savedQuoteSummary.fixed_price,
        estimated_price: savedQuoteSummary.estimated_price,
        min_price: savedQuoteSummary.min_price,
        max_price: savedQuoteSummary.max_price,
        notes: savedQuoteSummary.notes,
        quote_items: quoteItems.length > 0 ? quoteItems.map((item, index) => ({
          label: item.label,
          quantity: item.quantity,
          unit_label: item.unit_label,
          unit_price: item.unit_price,
          line_total: item.line_total,
          display_order: item.display_order || index
        })) : undefined
      };
    }

    try {
      const { data, error } = await supabase.functions.invoke('submit-proposal-response', {
        body: payload
      });

      if (error) {
        throw error;
      }

      setEditingSection(null);
      // Clear hash data after successful submission
      updateUrlHash('');
      
      // Invalidate job proposals queries to refresh MyJobOpportunities page
      queryClient.invalidateQueries({ queryKey: ['job-proposals'] });
      queryClient.invalidateQueries({ queryKey: ['job-proposal-counts'] });
      
      toast({
        title: "Success",
        description: "Your response has been sent successfully.",
      });
      navigate(PATH.ADMIN_MY_JOB_OPPORTUNITIES);
    } catch (error: any) {
      console.error('Error submitting proposal response:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to submit response. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusDisplay = (status: string) => {
    switch (status) {
      case 'AWAITING_RESPONSE':
        return { label: 'Awaiting Response', className: 'bg-gray-50 text-gray-500 border-gray-200' };
      case 'PROPOSED':
        return { label: 'Proposed', className: 'bg-green-50 text-green-700 border-green-200' };
      case 'DECLINED':
        return { label: 'Declined', className: 'bg-red-50 text-red-700 border-red-200' };
      case 'REQUESTED_INFO':
        return { label: 'Info Requested', className: 'bg-orange-50 text-orange-700 border-orange-200' };
      default:
        return { label: status, className: 'bg-gray-50 text-gray-500 border-gray-200' };
    }
  };

  // Use shared utility with translation support
  const getUrgencyDisplay = (urgency: string) => getUrgencyDisplayUtil(urgency, t);


  const renderQuoteDisplay = () => {
    if (!proposal) return null;

    // Show detailed quote if exists
    if (proposal.detailed_quote) {
      const quote = proposal.detailed_quote;
      return (
        <Card className="mb-4 bg-white border border-border transition-colors duration-200 hover:border-primary/20 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-gray-800">Current Quote</h4>
              <Badge variant="outline" className="text-xs">
                {quote.price_type} • {quote.status}
              </Badge>
            </div>
            
            <div className="text-lg font-semibold text-gray-900 mb-2">
              {quote.price_type === 'fixed' && `${currency}${quote.fixed_price}`}
              {quote.price_type === 'estimate' && `~${currency}${quote.estimated_price}`}
              {quote.price_type === 'range' && `${currency}${quote.min_price} - ${currency}${quote.max_price}`}
            </div>
            
            {quote.notes && (
              <p className="text-sm text-gray-600 mb-2">{quote.notes}</p>
            )}
            
            {quote.quote_items && quote.quote_items.length > 0 && (
              <div className="text-xs text-gray-500">
                {quote.quote_items.length} items in breakdown
              </div>
            )}
          </CardContent>
        </Card>
      );
    }

    // Show legacy quote if exists
    if (proposal.quote) {
      return (
        <Card className="mb-4 bg-white border border-border transition-colors duration-200 hover:border-primary/20 shadow-sm">
          <CardContent className="p-4">
            <h4 className="font-medium text-gray-800 mb-2">Current Quote</h4>
            <div className="text-lg font-semibold text-gray-900">
              {currency}{proposal.quote}
            </div>
          </CardContent>
        </Card>
      );
    }

    return null;
  };

  if (isLoading) {
    return (
      <div className="space-y-6 sm:space-y-8 animate-fade-in">
        <div className="space-y-4">
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-6 w-2/3" />
        </div>
      </div>
    );
  }

  if (error || !proposal) {
    return (
      <div className="space-y-6 sm:space-y-8 animate-fade-in">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-2" />
          <p className="text-destructive mb-4">Failed to load proposal details</p>
          <Button onClick={navigateBackWithHash}>
            Back to Job Opportunities
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8 animate-fade-in">
      {/* Header */}
      <div className="relative">
        <div className="flex items-center gap-4 mb-4">
          <Button
            variant="outline"
            onClick={navigateBackWithHash}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Job Opportunities
          </Button>
        </div>
        <h1 className="admin-section-title mb-2">Job Opportunity Details</h1>
        <p className="text-muted-foreground/80 text-lg">
          Review the service request and provide your response
        </p>
      </div>

      <div className="space-y-6">
        {/* Service Request with Attachments */}
        <div>
          <Card>
            <CardHeader className="flex flex-row items-start justify-between">
              <CardTitle className="text-base mb-0">Service Request</CardTitle>
              <div className="flex flex-col items-end gap-2">
                <Badge variant="outline" className={getStatusDisplay(proposal.status).className}>
                  {getStatusDisplay(proposal.status).label}
                </Badge>
                <Badge variant="outline" className={`text-xs ${getUrgencyDisplay(proposal.service_request.urgency).className}`}>
                  {getUrgencyDisplay(proposal.service_request.urgency).label}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-2">Request Details</h4>
                <p className="text-sm italic bg-gray-50 p-3 rounded-lg">
                  "{proposal.service_request.original_request}"
                </p>
              </div>

              <div className="space-y-4 text-sm">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Requested {formatDate(proposal.service_request.created_at)}</span>
                </div>
                
                {/* Town in bottom right */}
                {proposal.service_request.town && (
                  <div className="flex justify-end">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-muted-foreground" />
                      <span>{proposal.service_request.town}</span>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Attachments */}
          {proposal.service_request.attachments && proposal.service_request.attachments.length > 0 && (
            <div className="bg-white rounded-b-lg p-4 border border-t-0 border-gray-200 mt-2">
              <div className="flex items-center gap-2 mb-3 text-sm text-muted-foreground">
                <FileText className="w-4 h-4" />
                <span className="font-medium">Attachments ({proposal.service_request.attachments.length})</span>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
                {proposal.service_request.attachments.map((attachment) => (
                  <div 
                    key={attachment.id} 
                    className="group relative bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => window.open(attachment.image_url, '_blank')}
                  >
                    <div className="aspect-square relative">
                      <img
                        src={attachment.image_url}
                        alt={attachment.original_filename}
                        className="w-full h-full object-cover group-hover:opacity-95 transition-opacity"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2">
                        <p className="text-white text-xs truncate w-full" title={attachment.original_filename}>
                          {attachment.original_filename}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Current Quote Display */}
        {renderQuoteDisplay()}

        {/* Response Section - Conditional based on proposal status */}
        {proposal.status === 'AWAITING_RESPONSE' ? (
          <Card className="bg-white border border-border transition-colors duration-200 hover:border-primary/20 shadow-sm">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <MessageCircle className="w-4 h-4" />
                Your Response
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-2 block">
                  Response Status
                </label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, status: value as 'PROPOSED' | 'DECLINED' | 'REQUESTED_INFO' }))}
                >
                  <SelectTrigger className={`${
                    formData.status === 'PROPOSED' ? 'text-green-700' : 
                    formData.status === 'DECLINED' ? 'text-red-700' : 
                    formData.status === 'REQUESTED_INFO' ? 'text-blue-700' : 
                    ''
                  }`}>
                    <SelectValue placeholder="How would you like to respond?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PROPOSED" className="focus:bg-green-50">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-green-700">Make Proposal</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="DECLINED" className="focus:bg-red-50">
                      <div className="flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-red-600" />
                        <span className="text-red-700">Decline Request</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="REQUESTED_INFO" className="focus:bg-blue-50">
                      <div className="flex items-center gap-2">
                        <HelpCircle className="w-4 h-4 text-blue-600" />
                        <span className="text-blue-700">Request More Info</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Quote Section - Sub-item of Make Proposal */}
              {formData.status === 'PROPOSED' && (
                <Button
                  variant="ghost"
                  onClick={() => setEditingSection(editingSection === 'quote' ? null : 'quote')}
                  className={`w-full justify-between h-10 px-3 text-sm flex items-center border transition-colors ${
                    editingSection === 'quote' 
                      ? '!bg-gray-50 !text-gray-900 border-gray-300' 
                      : (savedQuoteSummary || proposal?.detailed_quote || quoteFormData?.price_type)
                        ? '!bg-green-50 !text-green-700 border-green-200 hover:!bg-green-100 hover:!text-green-700'
                        : 'text-muted-foreground hover:!text-green-700 hover:!bg-green-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <CornerDownRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <CurrencyIcon className="w-4 h-4" />
                    <span>
                      {savedQuoteSummary ? (
                        savedQuoteSummary.validation_error ? (
                          <span className="text-red-600 text-xs">⚠️ Validation error</span>
                        ) : (
                          <>
                            {savedQuoteSummary.price_type === 'fixed' && savedQuoteSummary.fixed_price && `= ${currency}${savedQuoteSummary.fixed_price} quote`}
                            {savedQuoteSummary.price_type === 'estimate' && savedQuoteSummary.estimated_price && `~ ${currency}${savedQuoteSummary.estimated_price} quote`}
                            {savedQuoteSummary.price_type === 'range' && savedQuoteSummary.min_price && (
                              savedQuoteSummary.max_price 
                                ? `Between ${currency}${savedQuoteSummary.min_price} - ${currency}${savedQuoteSummary.max_price}`
                                : `From ${currency}${savedQuoteSummary.min_price}`
                            )}
                            {savedQuoteSummary.price_type === 'no_quote' && 'No quote'}
                            {savedQuoteSummary.is_partial && <span className="text-xs ml-1">(incomplete)</span>}
                          </>
                        )
                      ) : proposal?.detailed_quote ? 'Edit quote' : 'Add quote?'}
                    </span>
                  </div>
                  {editingSection === 'quote' ? (
                    <ChevronUp className="w-4 h-4 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-4 h-4 flex-shrink-0" />
                  )}
                </Button>
              )}

              {/* Quote form - full width */}
              {formData.status === 'PROPOSED' && editingSection === 'quote' && (
                <div className="mt-3">
                  {/* Display validation error if exists */}
                  {savedQuoteSummary?.validation_error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-center gap-2 text-red-700 text-sm">
                        <AlertTriangle className="w-4 h-4" />
                        <span className="font-medium">Quote Validation Error</span>
                      </div>
                      <p className="text-red-600 text-sm mt-1">{savedQuoteSummary.validation_error}</p>
                    </div>
                  )}
                  <QuoteForm
                    proposalId={proposal.id}
                    existingQuote={proposal.detailed_quote}
                    formData={quoteFormData}
                    quoteItems={quoteItems}
                    showBreakdown={showBreakdown}
                    onFormDataChange={setQuoteFormData}
                    onQuoteItemsChange={setQuoteItems}
                    onShowBreakdownChange={setShowBreakdown}
                    onCollapse={() => setEditingSection(null)}
                    onReset={() => {
                      // Reset quote form data
                      setQuoteFormData({
                        price_type: '',
                        is_partial: false,
                        fixed_price: '',
                        estimated_price: '',
                        min_price: '',
                        max_price: '',
                        notes: ''
                      });
                      // Reset quote items
                      setQuoteItems([]);
                      // Reset breakdown visibility
                      setShowBreakdown(false);
                      // Clear saved quote summary
                      setSavedQuoteSummary(null);
                    }}
                  />
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-muted-foreground mb-2 block">
                  Response Message
                </label>
                <Textarea
                  placeholder={
                    formData.status === 'DECLINED' 
                      ? "Please explain why you're declining this request..."
                      : formData.status === 'REQUESTED_INFO'
                      ? "What additional information do you need?..."
                      : "Describe your proposed service, timeline, and approach..."
                  }
                  value={formData.response_message}
                  onChange={(e) => setFormData(prev => ({ ...prev, response_message: e.target.value }))}
                  className="min-h-[120px]"
                />
              </div>

              <Separator />

              <div className="flex gap-2">
                <Button 
                  onClick={handleSaveResponse}
                  disabled={isSubmitting || !formData.status || (savedQuoteSummary?.validation_error ? true : false)}
                  className="flex-1"
                >
                  {isSubmitting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <SendHorizontal className="h-4 w-4 mr-2" />
                  )}
                  Send Response
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          // Read-only view for already responded proposals
          <Card className="bg-gray-50 border border-gray-200">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                Response Already Submitted
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-white rounded-lg p-4 border">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">Response Status:</span>
                    <Badge variant="outline" className={getStatusDisplay(proposal.status).className}>
                      {getStatusDisplay(proposal.status).label}
                    </Badge>
                  </div>
                  
                  {proposal.responded_at && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600">Submitted:</span>
                      <span className="text-sm text-gray-900">{formatDate(proposal.responded_at)}</span>
                    </div>
                  )}
                  
                  {proposal.response_message && (
                    <div>
                      <span className="text-sm font-medium text-gray-600 block mb-2">Your Message:</span>
                      <div className="p-3 bg-gray-50 rounded border text-sm text-gray-900">
                        {proposal.response_message}
                      </div>
                    </div>
                  )}
                  
                  {/* Display quote information if exists */}
                  {(proposal.detailed_quote || proposal.quote) && (
                    <div>
                      <span className="text-sm font-medium text-gray-600 block mb-2">Quote Provided:</span>
                      <div className="p-3 bg-green-50 rounded border">
                        {proposal.detailed_quote ? (
                          <div className="text-sm">
                            <div className="font-medium text-green-900 mb-1">
                              {proposal.detailed_quote.price_type === 'fixed' && `${currency}${proposal.detailed_quote.fixed_price}`}
                              {proposal.detailed_quote.price_type === 'estimate' && `~${currency}${proposal.detailed_quote.estimated_price}`}
                              {proposal.detailed_quote.price_type === 'range' && `${currency}${proposal.detailed_quote.min_price} - ${currency}${proposal.detailed_quote.max_price}`}
                            </div>
                            {proposal.detailed_quote.notes && (
                              <div className="text-green-700 text-xs mt-1">{proposal.detailed_quote.notes}</div>
                            )}
                          </div>
                        ) : proposal.quote ? (
                          <div className="font-medium text-green-900 text-sm">
                            {currency}{proposal.quote}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-4">
                  Your response has been sent to the customer. You cannot modify it at this time.
                </p>
                <Button 
                  variant="outline" 
                  onClick={navigateBackWithHash}
                  className="w-full"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Job Opportunities
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Response History */}
        {proposal.responded_at && (
          <Card className="bg-white border border-border transition-colors duration-200 hover:border-primary/20 shadow-sm">
            <CardHeader>
              <CardTitle className="text-base">Response History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-sm text-muted-foreground">
                Last responded: {formatDate(proposal.responded_at)}
              </div>
              {proposal.response_message && (
                <div className="mt-2 p-3 bg-white border border-gray-200 rounded-lg">
                  <p className="text-sm">{proposal.response_message}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Confirmation Modal */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Your Response</DialogTitle>
            <DialogDescription>
              {formData.status === 'PROPOSED' && 'You are about to submit a proposal for this service request.'}
              {formData.status === 'DECLINED' && 'You are about to decline this service request.'}
              {formData.status === 'REQUESTED_INFO' && 'You are about to request more information from the customer.'}
            </DialogDescription>
          </DialogHeader>
          
          {/* Response Summary */}
          {formData.status && (
            <div className="py-4 border rounded-lg bg-gray-50">
              <h4 className="font-medium text-gray-900 mb-3 px-4">Response Summary</h4>
              
              <div className="space-y-3 px-4">
                {/* Response Type */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Response Type:</span>
                  <span 
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusDisplay(formData.status).className}`}
                  >
                    {getStatusDisplay(formData.status).label}
                  </span>
                </div>

                {/* Quote Information */}
                {formData.status === 'PROPOSED' && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Quote:</span>
                      <div className="text-sm">
                        {savedQuoteSummary ? (
                          <div className="text-right">
                            {savedQuoteSummary.validation_error ? (
                              <div className="text-red-600 text-sm">
                                <span className="font-medium">⚠️ Validation Error</span>
                                <div className="text-xs mt-1">{savedQuoteSummary.validation_error}</div>
                              </div>
                            ) : (
                              <div className="font-medium">
                                {savedQuoteSummary.price_type === 'fixed' && savedQuoteSummary.fixed_price && `${currency}${savedQuoteSummary.fixed_price}`}
                                {savedQuoteSummary.price_type === 'estimate' && savedQuoteSummary.estimated_price && `~${currency}${savedQuoteSummary.estimated_price}`}
                                {savedQuoteSummary.price_type === 'range' && savedQuoteSummary.min_price && (
                                  savedQuoteSummary.max_price 
                                    ? `${currency}${savedQuoteSummary.min_price} - ${currency}${savedQuoteSummary.max_price}`
                                    : `From ${currency}${savedQuoteSummary.min_price}`
                                )}
                                {savedQuoteSummary.price_type === 'no_quote' && 'No quote provided'}
                                {savedQuoteSummary.is_partial && <span className="text-xs text-orange-600 ml-1">(incomplete)</span>}
                              </div>
                            )}
                          </div>
                        ) : proposal?.detailed_quote ? (
                          <div className="text-right">
                            <div className="font-medium">
                              {proposal.detailed_quote.price_type === 'fixed' && `${currency}${proposal.detailed_quote.fixed_price}`}
                              {proposal.detailed_quote.price_type === 'estimate' && `~${currency}${proposal.detailed_quote.estimated_price}`}
                              {proposal.detailed_quote.price_type === 'range' && `${currency}${proposal.detailed_quote.min_price} - ${currency}${proposal.detailed_quote.max_price}`}
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-500">No quote provided</span>
                        )}
                      </div>
                    </div>
                    
                    {/* Quote Breakdown */}
                    {(() => {
                      const breakdownItems = savedQuoteSummary ? quoteItems : proposal?.detailed_quote?.quote_items;
                      const hasBreakdown = breakdownItems && breakdownItems.length > 0;
                      
                      return hasBreakdown && (
                        <div className="ml-4 pl-2 border-l-2 border-gray-200">
                          <div className="text-xs text-gray-500 mb-1 font-medium">
                            Breakdown ({breakdownItems.length} items):
                          </div>
                          <div className="space-y-1 max-h-24 overflow-y-auto">
                            {breakdownItems.map((item, index) => (
                              <div key={item.id || index} className="flex justify-between items-center text-xs">
                                <span className="text-gray-600 truncate flex-1 mr-2">
                                  {item.quantity > 1 ? `${item.quantity}× ` : ''}{item.label}
                                  {item.unit_label && ` (${item.unit_label})`}
                                </span>
                                <span className="font-medium text-gray-900 whitespace-nowrap">
                                  {currency}{item.line_total.toFixed(2)}
                                </span>
                              </div>
                            ))}
                            {breakdownItems.length > 3 && (
                              <div className="text-xs text-gray-400 italic pt-1">
                                {breakdownItems.length - 3} more items...
                              </div>
                            )}
                          </div>
                          {(() => {
                            const subtotal = breakdownItems.reduce((sum, item) => sum + item.line_total, 0);
                            return (
                              <div className="flex justify-between items-center text-xs font-medium text-gray-700 pt-1 mt-1 border-t border-gray-100">
                                <span>Subtotal:</span>
                                <span>{currency}{subtotal.toFixed(2)}</span>
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Response Message */}
                {formData.response_message.trim() && (
                  <div>
                    <span className="text-sm text-gray-600 block mb-1">Message:</span>
                    <div className="text-sm bg-white p-2 rounded border text-gray-900 max-h-20 overflow-y-auto">
                      {formData.response_message.trim()}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Show quote checkbox only for proposals without any quote data */}
          {(() => {
            const hasQuote = savedQuoteSummary || 
                           quoteFormData?.price_type || 
                           proposal?.detailed_quote;
            const showNoQuoteCheckbox = formData.status === 'PROPOSED' && !hasQuote;
            
            return showNoQuoteCheckbox && (
            <div className="py-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="modal-skip-quote"
                  checked={skipQuote}
                  onCheckedChange={(checked) => setSkipQuote(checked as boolean)}
                />
                <label
                  htmlFor="modal-skip-quote"
                  className="text-sm text-gray-600 cursor-pointer"
                >
                  I prefer not to provide a quote at this time
                </label>
              </div>
              {!skipQuote && (
                <p className="text-sm text-muted-foreground mt-2">
                  Note: A quote is recommended to help set clear expectations with your potential customer.
                </p>
              )}
            </div>
            );
          })()}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowConfirmModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmResponse}
              disabled={formData.status === 'PROPOSED' && !savedQuoteSummary && !proposal?.detailed_quote && !skipQuote}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobProposalDetail;